import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Star, DollarSign, Heart, Utensils } from "lucide-react";
import { API } from "../constants";
import { RestaurantMiniCard } from "../components/RestaurantMiniCard";

export const GalleryPage = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("");
  const [cuisineTypesList, setCuisineTypesList] = useState([]);
  const [specialtiesList, setSpecialtiesList] = useState([]);
  const [filters, setFilters] = useState({
    cuisineTypes: [],
    specialties: [],
    city: "",
    province: "",
    opinion: null,
    service: null,
    price: null,
    food: null,
    ambiance: null,
    favoritesOnly: false,
    mineOnly: false,
    priceMin: "",
    priceMax: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchRestaurants();
    fetchFavorites();
    fetchConfigData();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const res = await axios.get(`${API}/restaurants`);
      setRestaurants(res.data);
    } catch (err) {
      toast.error("Error al cargar restaurantes");
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`);
      setFavorites(res.data);
    } catch (err) {
      console.error("Error fetching favorites");
    }
  };

  const fetchConfigData = async () => {
    try {
      const [cuisineRes, specialtiesRes] = await Promise.all([
        axios.get(`${API}/config/cuisine-types`),
        axios.get(`${API}/config/specialties`)
      ]);
      setCuisineTypesList(cuisineRes.data);
      setSpecialtiesList(specialtiesRes.data);
    } catch (err) {
      console.error("Error loading config data");
    }
  };

  const toggleCuisineFilter = (type) => {
    const current = filters.cuisineTypes;
    if (current.includes(type)) {
      setFilters({ ...filters, cuisineTypes: current.filter(t => t !== type) });
    } else {
      setFilters({ ...filters, cuisineTypes: [...current, type] });
    }
  };

  const toggleSpecialtyFilter = (specialty) => {
    const current = filters.specialties;
    if (current.includes(specialty)) {
      setFilters({ ...filters, specialties: current.filter(s => s !== specialty) });
    } else {
      setFilters({ ...filters, specialties: [...current, specialty] });
    }
  };

  const setRatingFilter = (field, value) => {
    setFilters({ ...filters, [field]: filters[field] === value ? null : value });
  };

  // Filter restaurants
  const filteredRestaurants = restaurants.filter(r => {
    if (filters.cuisineTypes.length > 0 && !filters.cuisineTypes.includes(r.cuisine_type)) return false;
    if (filters.specialties.length > 0 && !filters.specialties.some(s => r.specialties?.includes(s))) return false;
    if (filters.city && r.city !== filters.city) return false;
    if (filters.province && r.province !== filters.province) return false;
    if (filters.mineOnly && r.is_owner !== true) return false;
    if (filters.favoritesOnly && !favorites.includes(r.id)) return false;
    if (filters.opinion && Math.round(r.avg_rating || 0) < filters.opinion) return false;
    if (filters.service && Math.round(r.avg_service || 0) < filters.service) return false;
    if (filters.price && Math.round(r.avg_price || 0) > filters.price) return false;
    if (filters.food && Math.round(r.avg_food || 0) < filters.food) return false;
    if (filters.ambiance && Math.round(r.avg_ambiance || 0) < filters.ambiance) return false;
    if (filters.priceMin || filters.priceMax) {
      if (!r.avg_price_per_person) return false;
      if (filters.priceMin && r.avg_price_per_person < parseFloat(filters.priceMin)) return false;
      if (filters.priceMax && r.avg_price_per_person > parseFloat(filters.priceMax)) return false;
    }
    return true;
  });

  // Sort restaurants
  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    if (sortBy === "opinion") {
      return (b.avg_rating || 0) - (a.avg_rating || 0);
    }
    if (sortBy === "price") {
      return (a.avg_price_per_person || 999) - (b.avg_price_per_person || 999);
    }
    return 0;
  });

  const clearFilters = () => {
    setFilters({
      cuisineTypes: [],
      specialties: [],
      city: "",
      province: "",
      opinion: null,
      service: null,
      price: null,
      food: null,
      ambiance: null,
      favoritesOnly: false,
      mineOnly: false,
      priceMin: "",
      priceMax: ""
    });
  };

  const hasActiveFilters = filters.cuisineTypes.length > 0 || filters.specialties.length > 0 || 
    filters.city || filters.province ||
    filters.opinion || filters.service || filters.price || filters.food || filters.ambiance || 
    filters.favoritesOnly || filters.mineOnly || filters.priceMin || filters.priceMax;

  // Get unique cities and provinces from restaurants
  const uniqueCities = [...new Set(restaurants.map(r => r.city).filter(Boolean))].sort();
  const uniqueProvinces = [...new Set(restaurants.map(r => r.province).filter(Boolean))].sort();

  return (
    <div className="min-h-screen pb-24" data-testid="gallery-page">
      <header className="sticky top-0 z-40 glass-nav p-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate("/")} data-testid="back-btn">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-xl font-semibold text-amber-500">Galería de Restaurantes</h1>
        </div>
      </header>

      <main className="p-4 max-w-6xl mx-auto">
        {/* Filters Section */}
        <section className="glass rounded-2xl p-4 mb-6">
          {/* Location Filters - Población y Provincia (at the top) */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="max-w-[200px]">
                <p className="text-xs font-bold text-zinc-400 mb-2">Población</p>
                <select
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-amber-500/50 rounded-lg text-sm text-amber-500 focus:border-amber-500 focus:outline-none"
                >
                  <option value="" className="text-zinc-400">Todas</option>
                  {uniqueCities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div className="max-w-[200px]">
                <p className="text-xs font-bold text-zinc-400 mb-2">Provincia</p>
                <select
                  value={filters.province}
                  onChange={(e) => setFilters({ ...filters, province: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-amber-500/50 rounded-lg text-sm text-amber-500 focus:border-amber-500 focus:outline-none"
                >
                  <option value="" className="text-zinc-400">Todas</option>
                  {uniqueProvinces.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              {/* Filter Buttons: Todos, Mis Restaurantes, Favoritos */}
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={() => setFilters({ ...filters, mineOnly: false })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !filters.mineOnly
                      ? "bg-amber-500 text-black"
                      : "bg-zinc-800 border border-amber-500/50 text-amber-500 hover:bg-amber-500/20"
                  }`}
                  data-testid="gallery-filter-all"
                >
                  Todos
                </button>
                <button
                  onClick={() => setFilters({ ...filters, mineOnly: true })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.mineOnly
                      ? "bg-amber-500 text-black"
                      : "bg-zinc-800 border border-amber-500/50 text-amber-500 hover:bg-amber-500/20"
                  }`}
                  data-testid="gallery-filter-mine"
                >
                  Mis Restaurantes
                </button>
                <button
                  onClick={() => setFilters({ ...filters, favoritesOnly: !filters.favoritesOnly })}
                  className="p-2 transition-colors hover:scale-110"
                  data-testid="gallery-filter-favorites"
                >
                  <Heart 
                    size={20} 
                    className={filters.favoritesOnly ? "fill-red-500 text-red-500" : "text-amber-500"} 
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Cuisine Type Filter */}
          <div className="mb-4">
            <p className="text-xs font-bold text-zinc-400 mb-2">Tipo de Cocina</p>
            <div className="flex flex-wrap gap-2">
              {cuisineTypesList.map((type) => (
                <button
                  key={type.id}
                  onClick={() => toggleCuisineFilter(type.name)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                    filters.cuisineTypes.includes(type.name)
                      ? "bg-amber-500/20 border border-amber-500 text-amber-500"
                      : "bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {type.name}
                </button>
              ))}
            </div>
          </div>

          {/* Specialties Filter */}
          <div className="mb-4">
            <p className="text-xs font-bold text-zinc-400 mb-2">Especialidades</p>
            <div className="flex flex-wrap gap-2">
              {specialtiesList.map((specialty) => (
                <button
                  key={specialty.id}
                  onClick={() => toggleSpecialtyFilter(specialty.name)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                    filters.specialties.includes(specialty.name)
                      ? "bg-amber-500/20 border border-amber-500 text-amber-500"
                      : "bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {specialty.name}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range Filter */}
          <div>
            <p className="text-xs font-bold text-zinc-400 mb-2">Precio Medio (€)</p>
            <div className="flex gap-2 items-center">
              <span className="text-zinc-400 text-xs">Mín.</span>
              <input
                type="number"
                value={filters.priceMin}
                onChange={(e) => setFilters({ ...filters, priceMin: e.target.value })}
                placeholder="0"
                className="w-16 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-300"
              />
              <span className="text-zinc-400 text-xs">Máx.</span>
              <input
                type="number"
                value={filters.priceMax}
                onChange={(e) => setFilters({ ...filters, priceMax: e.target.value })}
                placeholder="999"
                className="w-16 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-300"
              />
            </div>
          </div>

          {/* Rating Filters */}
          <div className="flex flex-wrap items-end gap-4 mt-4">
            {/* Opinion */}
            <div>
              <p className="text-xs font-bold text-zinc-400 mb-2">Opinión</p>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    onClick={() => setRatingFilter('opinion', val)}
                    className="p-0.5 rounded"
                  >
                    <Star size={14} className={val <= (filters.opinion || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600 hover:text-amber-500"} />
                  </button>
                ))}
              </div>
            </div>

            {/* Service */}
            <div>
              <p className="text-xs font-bold text-zinc-400 mb-2">Servicio</p>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    onClick={() => setRatingFilter('service', val)}
                    className="p-0.5 rounded"
                  >
                    <Star size={14} className={val <= (filters.service || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600 hover:text-amber-500"} />
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div>
              <p className="text-xs font-bold text-zinc-400 mb-2">Precio</p>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    onClick={() => setRatingFilter('price', val)}
                    className="p-0.5 rounded"
                  >
                    <DollarSign size={14} className={val <= (filters.price || 0) ? "text-emerald-500" : "text-zinc-600 hover:text-emerald-500"} />
                  </button>
                ))}
              </div>
            </div>

            {/* Food */}
            <div>
              <p className="text-xs font-bold text-zinc-400 mb-2">Comida</p>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    onClick={() => setRatingFilter('food', val)}
                    className="p-0.5 rounded"
                  >
                    <Star size={14} className={val <= (filters.food || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600 hover:text-amber-500"} />
                  </button>
                ))}
              </div>
            </div>

            {/* Ambiance */}
            <div>
              <p className="text-xs font-bold text-zinc-400 mb-2">Ambiente</p>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((val) => (
                  <button
                    key={val}
                    onClick={() => setRatingFilter('ambiance', val)}
                    className="p-0.5 rounded"
                  >
                    <Star size={14} className={val <= (filters.ambiance || 0) ? "fill-amber-500 text-amber-500" : "text-zinc-600 hover:text-amber-500"} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-4 text-xs text-amber-500 hover:underline"
            >
              Limpiar filtros
            </button>
          )}
        </section>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="skeleton aspect-[4/5] rounded-xl" />
            ))}
          </div>
        ) : sortedRestaurants.length === 0 ? (
          <div className="empty-state">
            <Utensils className="empty-state-icon" />
            <h3 className="text-xl font-semibold mb-2">Sin resultados</h3>
            <p className="text-zinc-400 mb-4">
              {hasActiveFilters ? "No hay restaurantes con estos filtros" : "Añade tu primer restaurante"}
            </p>
            {!hasActiveFilters && (
              <button
                onClick={() => navigate("/add")}
                className="px-6 py-3 bg-amber-500 text-black font-semibold rounded-xl btn-glow"
              >
                Añadir Restaurante
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-lg text-amber-500 font-semibold">{sortedRestaurants.length} Restaurantes</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-400 font-bold">Ordenar</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-300"
                >
                  <option value="">Sin ordenar</option>
                  <option value="opinion">Opinión (mayor a menor)</option>
                  <option value="price">Precio (menor a mayor)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedRestaurants.map((restaurant) => (
                <RestaurantMiniCard
                  key={restaurant.id}
                  restaurant={restaurant}
                  onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                  isFavorite={favorites.includes(restaurant.id)}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default GalleryPage;
